/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import {
  VerticalSpace,
  Steps,
  Input,
  Link,
  Text,
  Button,
} from '@brokerbay/design-components';
import Loading from '@app/react-common/Loading';
import { OnboardingPreLaunchModalContext } from 'contexts';
import { Space, Icon } from 'components';
import {
  formatDateStrWithYearShort,
  SUPPORT_EMAIL,
  UPGRADE_EMAIL,
} from 'utils';
import styled from 'styled-components';
import type { PortalWithAssociationDataConfig as OnboardingPortal } from '../../Router/onboarding/types';
import { shiftToUtc } from '../../Router/onboarding/util';
import CopyButton from './CopyButton';

const addScheme = (url: string) => {
  if (!url || url.startsWith('http') || url.includes('//')) return url;
  return `https://${url}`;
};

type LaunchStepsProps = {
  portalConfig?: OnboardingPortal;
  launchDate?: string;
  watchHowItWorksLink: string;
  onVideoLinkClicked: () => void;
  isAdmin: boolean;
  isAgent: boolean;
  isOrgPlanUpgraded: boolean;
};

const LaunchSteps = ({
  portalConfig,
  launchDate,
  watchHowItWorksLink,
  onVideoLinkClicked,
  isAdmin,
  isAgent,
  isOrgPlanUpgraded,
}: LaunchStepsProps) => {
  const { totalCount, configuredCount } = React.useContext(
    OnboardingPreLaunchModalContext,
  );

  if (!portalConfig) return <Loading />;

  const EMAIL = isOrgPlanUpgraded ? UPGRADE_EMAIL : SUPPORT_EMAIL;

  const STEPS_TO_RENDER = [
    {
      title: 'Take note of your launch date',
      component: (
        <StyledDateText $addPadding={!isAdmin}>
          {isAdmin && (
            <Text type="secondary">
              If you’d like to change or cancel your launch date, contact{' '}
              <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
            </Text>
          )}
          <DateText data-cy="launch-date-text">
            {launchDate
              ? formatDateStrWithYearShort(shiftToUtc(launchDate))
              : 'TBA (contact brokerage for more info)'}
          </DateText>
        </StyledDateText>
      ),
      key: 'launchDate',
      dataCy: 'org-onboarding-launch-date-step',
    },
    isAdmin &&
      portalConfig.adminTrainingLink && {
        title: 'Copy and share your admin / staff training link',
        component: (
          <VerticalSpace size={11}>
            <Text type="secondary">
              Ensure your office staff register for training before the official
              launch date
            </Text>
            <StyledInput
              value={portalConfig.adminTrainingLink}
              contentEditable={false}
              data-cy="org-onboarding-admin-training-link"
            />
            <Space wrap>
              <Link
                href={addScheme(portalConfig.adminTrainingLink)}
                target="_blank"
                data-cy="admin-training-new-tab-button"
              >
                Open in new tab
              </Link>
              <CopyButton
                textToCopy={addScheme(portalConfig.adminTrainingLink)}
                dataCy={'admin-training-copy-button'}
              />
            </Space>
          </VerticalSpace>
        ),
        key: 'adminTraining',
        dataCy: 'org-onboarding-admin-training-step',
      },
    portalConfig.agentTrainingLink && {
      title: isAgent
        ? 'Register for Training'
        : 'Copy and share your agent training link',
      component: (
        <VerticalSpace>
          <Text type="secondary">
            {`Ensure ${
              isAgent ? 'you' : 'your agents'
            } register for training before the official launch
            date`}
          </Text>
          <StyledInput
            value={portalConfig.agentTrainingLink}
            contentEditable={false}
            data-cy="org-onboarding-agent-training-link"
          />
          <Space wrap>
            <Link
              href={addScheme(portalConfig.agentTrainingLink)}
              target="_blank"
              data-cy="agent-training-new-tab-button"
            >
              Open in new tab
            </Link>
            <CopyButton
              textToCopy={addScheme(portalConfig.agentTrainingLink)}
              dataCy="agent-training-copy-button"
            />
          </Space>
        </VerticalSpace>
      ),
      key: 'agentTraining',
      dataCy: 'org-onboarding-agent-training-step',
    },
    {
      title: 'Setup Showing Instructions',
      component: (
        <VerticalSpace>
          <Text type="secondary">
            Watch a quick video to learn how to add showing instructions to all
            of your listings before launch!
          </Text>
          {totalCount > 0 && (
            <Text>
              {configuredCount} of {totalCount} listings completed (
              <Text strong>
                {Math.round((configuredCount / totalCount) * 100)}%
              </Text>
              )
            </Text>
          )}
          <Space wrap>
            <VideoButton type="link" onClick={onVideoLinkClicked}>
              <Icon type="antdPlayCircleFilled" color="primary" />
              Watch how it works (30s)
            </VideoButton>
            <CopyButton
              textToCopy={watchHowItWorksLink}
              dataCy="training-video-copy-button"
            >
              Copy video link
            </CopyButton>
          </Space>
        </VerticalSpace>
      ),
      key: 'showingInstructions',
      dataCy: 'org-onboarding-showing-instructions-step',
    },
  ];

  return (
    <>
      <StyledSteps
        direction="vertical"
        size="small"
        dataCy="org-onboarding-launch-steps"
      >
        {STEPS_TO_RENDER.filter(Boolean).map(step => (
          <StyledStep
            title={<StepTitleText strong>{step.title}</StepTitleText>}
            description={step.component}
            status="process"
            key={step.key}
            dataCy={step.dataCy}
          />
        ))}
      </StyledSteps>
    </>
  );
};

export default LaunchSteps;

const StepTitleText = styled(Text)`
  font-size: 14px;
`;

const StyledSteps = styled(Steps)`
  &&&& .ant-steps-item-tail {
    /* Hides indicator that passes through steps, not shown in design */
    display: none;
  }
`;

const StyledStep = styled(Steps.Step)`
  && .ant-steps-item-icon {
    margin-right: 8px;
  }
`;
const StyledInput = styled(Input)`
  padding: 0 3px;
`;
const DateText = styled(Text)`
  font-size: 24px;
  line-height: 28px;
  opacity: 0.85;
`;
const VideoButton = styled(Button)`
  && {
    padding-left: 0px;
  }
`;

const StyledDateText = styled(VerticalSpace)<{ $addPadding: boolean }>`
  ${({ $addPadding }) => $addPadding && 'padding-top: 40px;'}
  padding-bottom: 20px;
`;
